import React, {
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import cx from 'classnames';
import moment from 'moment';

import Button from '../../Button';
import { BackSm, Calendar, NextSm } from '../../Icons';
import InputText from '../InputText';

import DateList, { DAY_NAMES } from './DateList';
import type { DateRangePickerValueT } from './DateList';

function getShowMonths(month: number) {
  return [month - 1, month, month + 1, month + 2];
}

interface SelectPropsT {
  value: DateRangePickerValueT;
  onChange: (dates: DateRangePickerValueT) => void;
  onClear?: () => void;
}

const Select = ({ value, onChange, onClear = () => {} }: SelectPropsT) => {
  const [current, setCurrent] = useState<'startDate' | 'endDate'>('startDate');

  const curYearMonth = useMemo(() => {
    const date = value?.startDate ?? new Date();
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
    };
  }, [value]);

  const [showMonths, setShowMonths] = useState<number[]>(
    getShowMonths(curYearMonth.month)
  );

  const [dates, setDates] = useState<
    DateRangePickerValueT & { isNew: boolean }
  >({ ...value, isNew: false });

  const listRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const dateRefs = useMemo(
    () =>
      Array(4)
        .fill(0)
        .map(() => React.createRef<HTMLDivElement>()),
    []
  );

  useLayoutEffect(() => {
    function handleScroll(event: Event) {
      const { scrollTop, scrollHeight, clientHeight } =
        event.target as HTMLDivElement;

      if (scrollTop === 0 && clientHeight >= scrollHeight) {
        return;
      }

      // Complete scroll to the bottom
      if (scrollTop + clientHeight >= scrollHeight) {
        setShowMonths(getShowMonths(showMonths[2]));
        setTimeout(
          () => dateRefs[2].current.scrollIntoView({ block: 'end' }),
          10
        );
      }

      // Complete scroll to the top
      if (scrollTop === 0) {
        setShowMonths(getShowMonths(showMonths[0]));
        setTimeout(() => dateRefs[1].current?.scrollIntoView(), 10);
      }
    }

    listRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      listRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [listRef, showMonths, setShowMonths]);

  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      if (listRef?.current) {
        listRef.current.scrollTop = dateRefs[0].current.offsetHeight;
      }
    }, 10);

    return () => {
      clearTimeout(timer);
    };
  }, [listRef, dateRefs]);

  const handlePrevMonth = useCallback(() => {
    setShowMonths(getShowMonths(showMonths[0]));
  }, [showMonths]);

  const handleNextMonth = useCallback(() => {
    setShowMonths(getShowMonths(showMonths[2]));
  }, [showMonths]);

  const onSelectDate = useCallback(
    (date: Date) => {
      if (current === 'startDate') {
        setDates({ startDate: date, endDate: undefined, isNew: true });
        setCurrent('endDate');
      } else {
        if (moment(date).diff(dates.startDate, 'days') < 0) {
          setDates({ startDate: date, endDate: undefined, isNew: true });
        } else {
          setDates({ startDate: dates.startDate, endDate: date, isNew: true });
          setCurrent('startDate');
        }
      }
    },
    [dates]
  );

  useEffect(() => {
    if (footerRef?.current.clientHeight === 0 && dates.isNew) {
      onChange(dates);
    }
  }, [dates, footerRef?.current]);

  return (
    <>
      <div className="date-range-select">
        <div className="w-full md:absolute top-0 z-[1]">
          <div className="inline-flex md:hidden items-start gap-2 relative w-full">
            <InputText
              className={cx('flex-1', { disabled: current === 'endDate' })}
              inputClassName="w-[100px]"
              label="From"
              placeholder="MM/DD/YYYY"
              readOnly
              value={
                dates?.startDate
                  ? moment(dates?.startDate).format('MM/DD/YYYY')
                  : ''
              }
              rightIcon={
                <Calendar className="fill-gray-600" width={18} height={18} />
              }
            />
            <InputText
              className={cx('flex-1', { disabled: current === 'startDate' })}
              inputClassName="w-[100px]"
              label="To"
              placeholder="MM/DD/YYYY"
              readOnly
              value={
                dates?.endDate ? moment(dates.endDate).format('MM/DD/YYYY') : ''
              }
              rightIcon={
                <Calendar className="fill-gray-600" width={18} height={18} />
              }
            />
          </div>
          <div className="calendar-picker md:hidden py-1 border-b border-primary mt-[5px]">
            {DAY_NAMES.map((day: string) => (
              <div className="calendar-picker-cell" key={day}>
                <p className="text-gray-400">{day.charAt(0)}</p>
              </div>
            ))}
          </div>

          <div className="hidden md:flex items-center justify-between px-[15px] md:px-0">
            <BackSm
              className="cursor-pointer fill-uta-grey ml-3"
              onClick={handlePrevMonth}
              width={24}
              height={35}
            />
            <NextSm
              className="cursor-pointer fill-uta-grey mr-3"
              onClick={handleNextMonth}
              width={24}
              height={35}
            />
          </div>
        </div>
        <div className="date-list-container" ref={listRef}>
          <div className="flex flex-col md:flex-row gap-2.5 md:gap-[30px] w-fit md:w-max h-min mx-auto md:mx-0">
            {showMonths.map((val, index) => {
              const monthIndex = ((val % 12) + 12) % 12;

              return (
                <DateList
                  key={index}
                  ref={dateRefs[index]}
                  month={monthIndex}
                  year={curYearMonth.year + Math.floor(val / 12)}
                  onChange={onSelectDate}
                  dates={dates}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="flex md:hidden items-center justify-center p-[15px] w-full gap-2.5 bg-white border-t border-primary"
        ref={footerRef}
      >
        <Button
          onClick={() => {
            setDates(undefined);
            setCurrent('startDate');
            onClear();
          }}
          className="flex-1"
          disabled={dates === undefined}
        >
          Clear
        </Button>
        <Button
          onClick={() => {
            onChange(dates);
          }}
          variant="solid"
          className="flex-1"
          disabled={dates?.endDate === undefined || !dates?.isNew}
        >
          Apply
        </Button>
      </div>
    </>
  );
};

export default Select;
